<template>
  <teleport to="body">
    <!-- Overlay -->
    <transition name="fade">
      <div v-if="isOpen" @click="closeModal"
        class="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">

        <!-- Modal -->
        <div @click.stop
          class="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-md transform transition-all">

          <!-- En-tête -->
          <div class="flex items-center justify-between p-6 border-b dark:border-gray-700">
            <div class="flex items-center gap-3">
              <div
                class="w-10 h-10 bg-gradient-to-br from-teal-500 to-teal-700 rounded-xl flex items-center justify-center">
                <i class="las la-comment-medical text-white text-xl"></i>
              </div>
              <div>
                <h2 class="text-xl font-bold text-gray-900 dark:text-gray-100">
                  Nouvelle conversation
                </h2>
                <p class="text-xs text-gray-500 dark:text-gray-400">
                  Envoyez un message à un nouveau contact
                </p>
              </div>
            </div>
            <button @click="closeModal" type="button"
              class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors">
              <i class="las la-times text-2xl"></i>
            </button>
          </div>

          <!-- Formulaire -->
          <form @submit.prevent="handleSubmit" class="p-6 space-y-5">

            <!-- Numéro de téléphone -->
            <div>
              <label for="phone" class="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                Numéro de téléphone
                <span class="text-red-500">*</span>
              </label>
              <div class="relative">
                <div class="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <i class="las la-phone text-gray-400 text-lg"></i>
                </div>
                <input id="phone" v-model="form.phone" type="tel" required placeholder="Exemple: 22997123456" class="w-full pl-12 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 bg-white dark:bg-slate-700 placeholder-gray-400 dark:placeholder-gray-500
                         focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent
                         transition-all duration-200" :class="{ 'border-red-500': errors.phone }" />
              </div>
              <p v-if="errors.phone" class="mt-1 text-xs text-red-500 flex items-center gap-1">
                <i class="las la-exclamation-circle"></i>
                {{ errors.phone }}
              </p>
              <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Format: Code pays + numéro (ex: 22997123456)
              </p>
            </div>

            <!-- Message -->
            <div>
              <label for="message" class="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                Message
                <span class="text-red-500">*</span>
              </label>
              <div class="relative">
                <textarea id="message" v-model="form.message" required rows="4"
                  placeholder="Écrivez votre message ici..." class="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 bg-white dark:bg-slate-700 placeholder-gray-400 dark:placeholder-gray-500
                         focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent
                         transition-all duration-200 resize-none"
                  :class="{ 'border-red-500': errors.message }"></textarea>
                <div class="absolute bottom-3 right-3 text-xs text-gray-400 dark:text-gray-500">
                  {{ form.message?.length || 0 }} / 1000
                </div>
              </div>
              <p v-if="errors.message" class="mt-1 text-xs text-red-500 flex items-center gap-1">
                <i class="las la-exclamation-circle"></i>
                {{ errors.message }}
              </p>
            </div>

            <!-- Message d'erreur global -->
            <transition name="slide-down">
              <div v-if="showError" class="bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500 p-4 rounded-r-lg">
                <div class="flex items-start gap-2">
                  <i class="las la-exclamation-circle text-red-500 text-lg flex-shrink-0 mt-0.5"></i>
                  <div class="flex-1">
                    <h4 class="text-sm font-semibold text-red-800 dark:text-red-300">Erreur</h4>
                    <p class="text-sm text-red-700 dark:text-red-400 mt-0.5">{{ errorMessage }}</p>
                  </div>
                </div>
              </div>
            </transition>

            <!-- Message de succès -->
            <transition name="slide-down">
              <div v-if="showSuccess"
                class="bg-green-50 dark:bg-green-900/20 border-l-4 border-green-500 p-4 rounded-r-lg">
                <div class="flex items-start gap-2">
                  <i class="las la-check-circle text-green-500 text-lg flex-shrink-0 mt-0.5"></i>
                  <div class="flex-1">
                    <h4 class="text-sm font-semibold text-green-800 dark:text-green-300">Succès</h4>
                    <p class="text-sm text-green-700 dark:text-green-400 mt-0.5">Message envoyé avec succès !</p>
                  </div>
                </div>
              </div>
            </transition>

            <!-- Boutons d'action -->
            <div class="flex gap-3 pt-2">
              <button @click="closeModal" type="button" class="flex-1 px-4 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-semibold rounded-lg
                       hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors">
                Annuler
              </button>
              <button type="submit" :disabled="isLoading" :class="[
                'flex-1 px-4 py-3 font-semibold rounded-lg text-white transition-all transform',
                isLoading
                  ? 'bg-teal-400 cursor-not-allowed'
                  : 'bg-teal-600 hover:bg-teal-700 hover:shadow-lg active:scale-[0.98]'
              ]">
                <span v-if="isLoading" class="flex items-center justify-center gap-2">
                  <svg class="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z">
                    </path>
                  </svg>
                  Envoi...
                </span>
                <span v-else class="flex items-center justify-center gap-2">
                  <i class="las la-paper-plane text-lg"></i>
                  Envoyer
                </span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<script>
import { mapActions } from 'vuex';

export default {
  name: 'NewConversationModal',
  props: {
    isOpen: {
      type: Boolean,
      default: false
    }
  },
  emits: ['close', 'success'],
  data() {
    return {
      form: {
        phone: '',
        message: ''
      },
      errors: {
        phone: '',
        message: ''
      },
      isLoading: false,
      showError: false,
      errorMessage: '',
      showSuccess: false
    }
  },
  methods: {
    ...mapActions({
      getConversations: 'conversations/list/getAll',
      newConversation: 'conversations/create/create'
    }),

    closeModal() {
      if (this.isLoading) return;
      this.resetForm();
      this.$emit('close');
    },

    resetForm() {
      this.form = {
        phone: '',
        message: ''
      };
      this.errors = {
        phone: '',
        message: ''
      };
      this.showError = false;
      this.showSuccess = false;
      this.errorMessage = '';
    },

    validateForm() {
      this.errors = {
        phone: '',
        message: ''
      };

      let isValid = true;

      // Validation du numéro
      if (!this.form.phone) {
        this.errors.phone = 'Le numéro de téléphone est requis';
        isValid = false;
      }

      // Validation du message
      if (!this.form.message || this.form.message.trim().length === 0) {
        this.errors.message = 'Le message est requis';
        isValid = false;
      } else if (this.form.message.length > 1000) {
        this.errors.message = 'Le message ne peut pas dépasser 1000 caractères';
        isValid = false;
      }

      return isValid;
    },

    async handleSubmit() {
      // Validation
      if (!this.validateForm()) {
        return;
      }

      this.isLoading = true;
      this.showError = false;
      this.showSuccess = false;

      try {
        this.newConversation({
          phone: this.form.phone,
          content: this.form.message,
          reply_id : null
        });

        // Recharger la liste des conversations
        await this.getConversations();
        // Fermer le modal après 1.5s
        setTimeout(() => {
          this.closeModal();
        }, 1500);

      } catch (error) {
        console.error('Erreur:', error);
        this.showError = true;
        this.errorMessage = error.message || 'Une erreur est survenue lors de l\'envoi';
      } finally {
        this.isLoading = false;
      }
    }
  },
  watch: {
    isOpen(newVal) {
      if (!newVal) {
        this.resetForm();
      }
    }
  }
}
</script>

<style scoped>
/* Animations */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.fade-enter-active .bg-white,
.fade-leave-active .bg-white {
  transition: all 0.3s ease;
}

.fade-enter-from .bg-white {
  opacity: 0;
  transform: scale(0.95) translateY(-20px);
}

.fade-leave-to .bg-white {
  opacity: 0;
  transform: scale(0.95) translateY(20px);
}

.slide-down-enter-active,
.slide-down-leave-active {
  transition: all 0.3s ease-out;
}

.slide-down-enter-from {
  opacity: 0;
  transform: translateY(-10px);
}

.slide-down-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}
</style>